/*
 * @Copyright 2024. Institute for Future Intelligence, Inc.
 */

import React from 'react';
import { reportClientError } from './errorReport.ts';
import { useStore } from './stores/common.ts';
import { isReloadingForNewBuild, isStaleChunkError, reloadForNewBuild } from './staleBuild.ts';

interface ErrorPageProps {
  children?: React.ReactNode;
}

interface ErrorPageState {
  hasError: boolean;
  error: Error | null;
  componentStack: string | null;
  reloading?: boolean; // a stale-build chunk error: reloading onto the new version
}

class ErrorPage extends React.Component<ErrorPageProps, ErrorPageState> {
  constructor(props: ErrorPageProps) {
    super(props);
    this.state = { hasError: false, error: null, componentStack: null };
  }

  static getDerivedStateFromError(error: Error) {
    // Update state so the next render will show the fallback UI.
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // A tab that outlived a deploy asked for a code chunk the new build no
    // longer has. Not a bug to report: reload onto the new version (at most
    // once a minute — see staleBuild.ts), keeping the user's local work.
    if (isReloadingForNewBuild() || (isStaleChunkError(error?.message) && reloadForNewBuild())) {
      this.setState({ error, reloading: true });
      return;
    }
    // Keep the details so we can show them on the fallback page, and always
    // log to the console so a captured stack survives even after a reload.
    this.setState({ error, componentStack: errorInfo.componentStack ?? null });
    console.error('AIMS render error:', error, errorInfo);
    // Email the trace to the maintainer in the background (best-effort; the
    // reporter never throws and no-ops when signed out).
    let title: string | undefined;
    try {
      title = useStore.getState().projectState?.title ?? undefined;
    } catch {
      /* store not ready */
    }
    reportClientError({
      source: 'render',
      message: error?.message,
      stack: error?.stack,
      componentStack: errorInfo.componentStack ?? undefined,
      title,
    });
  }

  // The full report a user can copy and send us: message, JS stack, and the
  // React component stack that located the throwing component.
  errorDetails() {
    const { error, componentStack } = this.state;
    const parts = [
      `Message: ${error?.message ?? 'unknown'}`,
      error?.stack ? `\nStack:\n${error.stack}` : '',
      componentStack ? `\nComponent stack:${componentStack}` : '',
      `\nURL: ${window.location.href}`,
      `\nUser agent: ${navigator.userAgent}`,
    ];
    return parts.filter(Boolean).join('\n');
  }

  copyDetails = () => {
    const text = this.errorDetails();
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).catch(() => {
        /* clipboard blocked (insecure context / permissions) — the <pre> is still selectable */
      });
    }
  };

  // A stale-build chunk error only needs a plain reload (the new build has the
  // chunk); clearing localStorage would also throw away the user's local work.
  clearCacheAndReload = () => {
    if (!isStaleChunkError(this.state.error?.message)) localStorage.clear();
    window.location.reload();
  };

  render() {
    if (this.state.hasError && this.state.reloading) {
      return (
        <div style={{ marginTop: 10, marginLeft: 20, marginRight: 20 }}>
          <p>AIMS has been updated — loading the new version…</p>
        </div>
      );
    }
    if (this.state.hasError) {
      return (
        <div style={{ marginTop: 10, marginLeft: 20, marginRight: 20 }}>
          <h1>Error</h1>
          <p>
            Something went wrong. Please click <b>Refresh</b> to clear the cache and reload the page. If the problem
            keeps happening, click <b>Copy details</b> and send them to us so we can fix it.
          </p>
          <p>
            <button onClick={this.clearCacheAndReload}>Refresh</button>{' '}
            <button onClick={this.copyDetails}>Copy details</button>
          </p>
          <pre
            style={{
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              maxHeight: '50vh',
              overflow: 'auto',
              padding: 10,
              background: '#f6f6f6',
              border: '1px solid #ddd',
              borderRadius: 4,
              fontSize: 12,
              color: '#a00',
              userSelect: 'text',
            }}
          >
            {this.errorDetails()}
          </pre>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorPage;
